import React, { useMemo } from 'react';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';
import { resolveBrandLogoUrl } from '../lib/brandLogo';

/**
 * Renderiza logomarca do escritório (sem adaptação de fundo).
 */
export function BrandLogoImage({ src, alt, className, style, wrapClassName, ...rest }) {
  if (!src) return null;
  const img = <img src={src} alt={alt} className={className} style={style} {...rest} />;
  if (wrapClassName) {
    return <div className={wrapClassName}>{img}</div>;
  }
  return img;
}

/**
 * BrandMark — monograma OU logo do escritório.
 * Aceita `logoUrl` directo, ou `logoUrlLight`/`logoUrlDark` (escolhe pelo tema).
 */
export default function BrandMark({
  name = 'Dragon Tech',
  size = 32,
  variant = 'square',
  className,
  showName = true,
  nameClassName,
  collapsed = false,
  logoUrl,
  logoUrlLight,
  logoUrlDark,
  logoMaxWidth,
  ...rest
}) {
  void rest;
  const { resolved } = useTheme();
  const theme = resolved === 'dark' ? 'dark' : 'light';

  const resolvedLogo = useMemo(() => {
    if (logoUrl) return logoUrl;
    return resolveBrandLogoUrl(
      { logo_url_light: logoUrlLight, logo_url_dark: logoUrlDark },
      theme,
    );
  }, [logoUrl, logoUrlLight, logoUrlDark, theme]);

  const initial = useMemo(() => {
    const trimmed = (name || 'A').trim();
    return trimmed.charAt(0).toUpperCase() || 'A';
  }, [name]);

  const boxSize = useMemo(() => {
    const s = Number(size) || 32;
    if (collapsed) return Math.max(22, Math.round(s * 0.85));
    return s;
  }, [size, collapsed]);

  const fontSize = Math.max(11, Math.round(boxSize * 0.46));

  if (resolvedLogo) {
    const logoHeight = Math.round(boxSize * 1.25);
    const maxW = logoMaxWidth || Math.round(boxSize * 5.5);
    return (
      <div className={cn('flex items-center min-w-0', className)}>
        <BrandLogoImage
          key={`${theme}:${resolvedLogo}`}
          src={resolvedLogo}
          alt={name}
          className="object-contain shrink-0 select-none"
          style={{
            height: logoHeight,
            maxHeight: logoHeight,
            width: 'auto',
            maxWidth: maxW,
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn('flex items-center gap-2.5 min-w-0', className)}>
      <div
        className="relative rounded-[8px] flex items-center justify-center shrink-0 select-none overflow-hidden"
        style={{
          width: variant === 'wide' ? boxSize : boxSize,
          height: boxSize,
          fontSize,
          fontFamily: "'Geist', ui-sans-serif, system-ui, sans-serif",
          fontWeight: 700,
          letterSpacing: '-0.02em',
          background: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
        }}
        aria-hidden
      >
        <span className="relative z-10">{initial}</span>
      </div>
      {showName && (
        <span
          className={cn(
            'font-display font-semibold text-[15px] tracking-[-0.018em] truncate',
            nameClassName,
          )}
        >
          {name}
        </span>
      )}
    </div>
  );
}
